import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@/shared/ui';
import {
  APPOINTMENT_TYPE_COLOR_PRESETS,
  DEFAULT_APPOINTMENT_TYPE_COLOR,
  normalizeAppointmentTypeColor,
} from '../utils/appointmentTypeColor';

interface AppointmentTypeColorFieldProps {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}

const SWATCH_SIZE = 28;

const AppointmentTypeColorField: React.FC<AppointmentTypeColorFieldProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('appointment');
  const normalized = normalizeAppointmentTypeColor(value) ?? DEFAULT_APPOINTMENT_TYPE_COLOR;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          display: 'block',
          mb: 1,
          fontSize: 12,
          lineHeight: '12px',
          color: 'text.secondary',
        }}
      >
        {t('catalog.fields.color')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {APPOINTMENT_TYPE_COLOR_PRESETS.map((preset) => {
          const selected = normalized.toUpperCase() === preset.toUpperCase();
          return (
            <Box
              key={preset}
              component="button"
              type="button"
              aria-label={preset}
              aria-pressed={selected}
              onClick={() => !disabled && onChange(preset)}
              disabled={disabled}
              sx={{
                width: SWATCH_SIZE,
                height: SWATCH_SIZE,
                p: 0,
                borderRadius: '50%',
                bgcolor: preset,
                border: 2,
                borderColor: selected ? 'text.primary' : 'divider',
                outline: selected ? '2px solid' : 'none',
                outlineColor: 'background.paper',
                outlineOffset: -4,
                cursor: disabled ? 'default' : 'pointer',
                flexShrink: 0,
                opacity: disabled ? 0.5 : 1,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default AppointmentTypeColorField;
